import { IBulkOfferingItem } from '@monetize/types/app';
import { pluralize } from '@monetize/utils/core';
import {
  MdCheckCircle,
  MdError,
  MdErrorOutline,
  MdRadioButtonUnchecked,
} from 'react-icons/md';
import {
  MBox,
  MFlex,
  MGrid,
  MIcon,
  MSpinner,
  MText,
} from '../../../../../components/Monetize';
import { BulkOfferingAddStatus } from './hook/useBulkOfferingAddRun';
import { BulkRateEntry } from './utils/bulkOfferingUtils';

const STATUS_GRID_COLUMNS =
  '20px minmax(0, 1fr) minmax(0, 1.1fr) minmax(0, 0.8fr)';

const STATUS_TEXT: Record<BulkOfferingAddStatus, string> = {
  queued: 'Queued',
  adding: 'Adding…',
  added: 'Added',
  // The API error itself is in the toast that the create request raises
  failed: 'Not added',
  unconfirmed: 'Check the quote',
};

const STATUS_TEXT_COLOR: Record<BulkOfferingAddStatus, string> = {
  queued: 'fg.subtle',
  adding: 'action.primary',
  added: 'fg.muted',
  failed: 'action.danger',
  unconfirmed: 'action.danger',
};

const StatusGlyph = ({ status }: { status: BulkOfferingAddStatus }) => {
  switch (status) {
    case 'adding':
      return <MSpinner size="sm" color="action.primary" />;
    case 'added':
      return <MIcon as={MdCheckCircle} boxSize={5} color="action.success" />;
    case 'failed':
      return <MIcon as={MdError} boxSize={5} color="action.danger" />;
    case 'unconfirmed':
      return <MIcon as={MdErrorOutline} boxSize={5} color="action.danger" />;
    default:
      return (
        <MIcon as={MdRadioButtonUnchecked} boxSize={5} color="fg.subtle" />
      );
  }
};

export const BulkOfferingAddStatusHeader = ({
  addStatuses,
  isRunning,
  runSize,
}: {
  addStatuses: Record<string, BulkOfferingAddStatus>;
  isRunning: boolean;
  /** Offerings in the run in flight, fewer than the total on a retry */
  runSize: number;
}) => {
  const statuses = Object.values(addStatuses);
  const totalCount = statuses.length;
  const addedCount = statuses.filter((status) => status === 'added').length;
  const notAddedCount = totalCount - addedCount;

  return (
    <MFlex justify="space-between" align="center" gap={4}>
      <MText fontWeight="semibold">
        {isRunning
          ? `Adding ${runSize} ${pluralize('offering', runSize)}`
          : `${notAddedCount} ${pluralize('offering', notAddedCount)} not added`}
      </MText>
      <MText as="output" fontSize="xs" fontWeight="normal" color="fg.muted">
        {addedCount} of {totalCount} added
      </MText>
    </MFlex>
  );
};

/**
 * Read-only stand-in for the review rows while offerings are being added, so
 * the user can see which have landed and which failed.
 */
export const BulkOfferingAddStatusList = ({
  bulkRate,
  offerings,
  addStatuses,
}: {
  bulkRate: BulkRateEntry[];
  offerings: IBulkOfferingItem[];
  addStatuses: Record<string, BulkOfferingAddStatus>;
}) => (
  <MBox>
    <MGrid
      templateColumns={STATUS_GRID_COLUMNS}
      gap={3}
      mb={2}
      fontSize="xs"
      fontWeight="semibold"
      color="fg.muted"
    >
      <MBox />
      <MText>Offering</MText>
      <MText>Rate</MText>
      <MText>Status</MText>
    </MGrid>
    {bulkRate.map(({ offeringId, rateId }) => {
      const offering = offerings.find(({ id }) => id === offeringId);
      const rateName = offering?.rates?.find(({ id }) => id === rateId)?.name;
      const status = addStatuses[offeringId] ?? 'queued';
      const isQueued = status === 'queued';

      return (
        <MGrid
          key={offeringId}
          templateColumns={STATUS_GRID_COLUMNS}
          gap={3}
          alignItems="center"
          minH={9}
          data-testid={`bulk-offering-status-${offeringId}`}
        >
          <MFlex align="center">
            <StatusGlyph status={status} />
          </MFlex>
          <MText lineClamp={1} color={isQueued ? 'fg.subtle' : 'fg.primary'}>
            {offering?.name}
          </MText>
          <MText lineClamp={1} color={isQueued ? 'fg.subtle' : 'fg.muted'}>
            {rateName}
          </MText>
          <MText fontSize="xs" color={STATUS_TEXT_COLOR[status]}>
            {STATUS_TEXT[status]}
          </MText>
        </MGrid>
      );
    })}
  </MBox>
);
